import { useEffect, useRef, useState } from "react";

import { Alert, Box, Snackbar, Stack } from "@mui/material";
import { Button, Trash01Icon, Typography, UploadCloud02Icon } from "components";
import { base, gray } from "configs/colors";
import { shadows } from "configs/shadows";
import i18n from "translations/i18n";
import { rem } from "utils";
import { getPrimaryColor } from "utils/theme";

interface Props {
  fileExtensions?: Array<string>;
  fileSize?: number; // MB
  description?: string;
  onChange?: (file: File | null, callback?: () => void) => void;
  loading?: boolean;
  supportFileMessage?: string;
  initFile?: File | null;
  error?: boolean;
}

const Upload: React.FC<Props> = ({
  fileExtensions,
  fileSize,
  description,
  onChange,
  loading = false,
  error = false,
  initFile = null,
}) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadErrMsg = useRef<string>("");
  const [openAlert, setOpenAlert] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(initFile);

  useEffect(() => {
    if (initFile) {
      setUploadedFile(initFile);
    }
  }, [initFile]);

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleFileClear = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setUploadedFile(null);
    onChange?.(null);
  };

  const handleFinderOpen = () => {
    const inputTarget = inputRef.current;
    if (!inputTarget || uploadedFile) return;
    inputTarget.click();
  };

  const fileHandler = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    const fileName = file.name;

    reader.onloadend = () => {
      const size = file.size / 1048576;
      if (typeof fileSize === "number" && size > fileSize) {
        uploadErrMsg.current = `Incorrect file size. It must be less than or equal to ${fileSize}MB`;
        setOpenAlert(false);
        handleFileClear();
        return;
      }
      onChange?.(file, () => {
        handleFileClear();
        return fileName;
      });
      setUploadedFile(file);
    };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileHandler(file);
  };

  useEffect(
    function clearUploadErrMsg() {
      if (openAlert) return;
      uploadErrMsg.current = "";
    },
    [openAlert]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const dragOverEventHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const dropEventHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      fileHandler(file);
    };

    container.addEventListener("dragover", dragOverEventHandler, false);
    container.addEventListener("drop", dropEventHandler, false);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener("dragover", dragOverEventHandler, false);
      container.removeEventListener("drop", dropEventHandler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadFile = () => {
    if (!uploadedFile) return;
    const url = URL.createObjectURL(uploadedFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = uploadedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box
        ref={containerRef}
        padding="24px"
        borderRadius="12px"
        border="1px solid rgba(208, 213, 221, 1)"
        borderColor={error ? "#D92D20" : ""}
        textAlign="center"
        sx={{
          background: base.White,
          cursor: "pointer",
          transition: "opacity .3s",
          "&:hover": uploadedFile ? undefined : { opacity: 0.7 },
        }}
        onClick={handleFinderOpen}
        draggable
      >
        {uploadedFile ? (
          <>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Typography
                fontWeight={600}
                type="TextMD"
                color="brown.600"
                onClick={handleDownloadFile}
              >
                {uploadedFile?.name}
              </Typography>
              <Button
                variant="text"
                size="icon-sm"
                color="error"
                onClick={handleFileClear}
                disabled={loading}
              >
                <Trash01Icon />
              </Button>
            </Stack>
            {loading && (
              <Stack mt={rem(12)} direction="row" justifyContent="center">
                <div className="spinnerCube">
                  <div />
                  <div />
                  <div />
                  <div />
                  <div />
                  <div />
                </div>
              </Stack>
            )}
          </>
        ) : (
          <>
            <Box
              display="inline-flex"
              padding="8px"
              marginBottom="16px"
              borderRadius="8px"
              border={`1px solid ${gray[300]}`}
              boxShadow={shadows.xs}
              sx={{ background: base.White }}
            >
              <UploadCloud02Icon fontSize="medium" sx={{ color: gray[600] }} />
            </Box>
            <Stack rowGap={rem(8)}>
              <Typography type="TextSM" color={gray[600]} textAlign="center">
                <span
                  style={{ color: getPrimaryColor()[700], fontWeight: 600 }}
                >
                  {i18n.t("clickToUpload")}
                </span>
                &nbsp;{i18n.t("orDragAndDrop")}
              </Typography>
              <Typography
                type="TextXS"
                color={gray[600]}
                marginTop="-6px"
                textAlign="center"
                dangerouslySetInnerHTML={{ __html: description || "" }}
              />
              <Typography
                type="TextXS"
                color={gray[600]}
                textAlign="center"
                marginTop="-8px"
              >
                File size should be maximum <b>20mb</b> and{" "}
                <b> shouldn’t be password protected</b>
              </Typography>
            </Stack>
            <input
              style={{ display: "none" }}
              ref={inputRef}
              type="file"
              accept={fileExtensions ? fileExtensions.join(", ") : undefined}
              onChange={handleFileChange}
            />
          </>
        )}
      </Box>

      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="error"
          sx={{ width: "100%" }}
        >
          {uploadErrMsg.current}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Upload;
